import React, { useState, useEffect } from 'react';
import ColumnaBotoneraItem from '../ColumnaBotoneraItem.js';
import FilaTorneoParticipanteEditable from './FilaTorneoParticipanteEditable.js';
import IconoMovimiento from '../../UI/IconoMovimiento.js';
import IconoTitulo from '../../UI/IconoTitulo.js';
import { IconoOrganizador } from '../../UI/IconoOrganizador.js';
import { NombreEquipoTorneo } from '../../funciones/FuncionesEquipo.js';
import * as UI from '../../constantes/UI.js';
import styles from '../CuadroListaEditable.module.css';
import styleCols from './TablaTorneoParticipantes.module.css';

const TablaTorneoParticipantes = (props) => {
  const lista = props.lista || []; // Asegurar que lista es al menos un array vacío
  const idElegido = props.idElegido;
  const onEditarItem = props.onEditarItem;
  const onEliminarItem = props.onEliminarItem;
  const onGuardarNuevo = props.onGuardarNuevo;
  const onGuardarTodo = props.onGuardarTodo;
  const itemsEditando = React.useMemo(() => props.itemsEditando ?? {}, [props.itemsEditando]);
  const onGuardarEdicion = props.onGuardarEdicion;
  const onCancelarEdicionItem = props.onCancelarEdicionItem;
  const torneo = props.contexto?.torneo;
  const equiposDisponibles = props.contexto?.equiposDisponibles ?? null;
  const cantidadParticipantes = torneo?.cantidadParticipantes || 0;
  const { esMobile } = UI.useUI();

  // Calcular cuántas filas editables mostrar (necesario antes de los hooks)
  const cantidadCargados = lista.length;
  const filasEditablesNecesarias =
    cantidadParticipantes > cantidadCargados ? cantidadParticipantes - cantidadCargados : 0;

  const [filasEditando, setFilasEditando] = useState([]);
  const [guardandoTodo, setGuardandoTodo] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [itemsEditandoActual, setItemsEditandoActual] = useState({});

  // Reiniciar el estado de las filas cuando cambia la cantidad necesaria
  useEffect(() => {
    setFilasEditando(Array.from({ length: filasEditablesNecesarias }, () => null));
  }, [filasEditablesNecesarias]);

  // Sincronizar itemsEditandoActual cuando cambia itemsEditando (añadidos / eliminados)
  useEffect(() => {
    setItemsEditandoActual((prev) => {
      const resultado = {};
      for (const [id, item] of Object.entries(itemsEditando)) {
        // Conservar cambios locales si ya existen; si es nuevo, usar el valor cargado
        resultado[id] = prev[id] ?? item;
      }
      return resultado;
    });
  }, [itemsEditando]);

  const onItemChangeHandler = (index, item) => {
    setFilasEditando((prev) => {
      const nueva = [...prev];
      nueva[index] = item;
      return nueva;
    });
  };

  const onItemChangeEdicion = (id, updatedItem) => {
    setItemsEditandoActual((prev) => ({ ...prev, [id]: updatedItem }));
  };

  const handleGuardarTodo = async () => {
    setGuardandoTodo(true);
    try {
      const editedItems = Object.values(itemsEditandoActual).filter((item) => item?.equipo);
      const newItems = filasEditando.filter((item) => item?.equipo);
      if (editedItems.length > 0 || newItems.length > 0) {
        await onGuardarTodo(newItems, editedItems);
        setResetKey((k) => k + 1);
      }
    } finally {
      setGuardandoTodo(false);
    }
  };

  const hayFilasConDatos = filasEditando.some((item) => item?.equipo);
  const hayItemsEditandoConDatos = Object.values(itemsEditandoActual).some((item) => item?.equipo);
  const hayDatosParaGuardar = hayItemsEditandoConDatos || hayFilasConDatos;

  const esIdElegido = (idItem) => {
    return Number(idElegido) === Number(idItem);
  };

  // En modo consulta (sin filas editables o sin permiso para editar), ocultar columnas título y movimiento
  const modoConsulta = filasEditablesNecesarias === 0 || !onGuardarNuevo;
  const colSpanGuardarTodo = (modoConsulta ? 8 : 10) - (esMobile ? 2 : 0);

  // Si no hay participantes cargados ni filas editables, no mostrar nada
  if (cantidadCargados === 0 && filasEditablesNecesarias === 0) {
    return null;
  }

  return (
    <React.Fragment>
      <tr className={`${styles.titulos}`} key="0">
        <td className={`${styleCols.posicion}`} title="Posición Final">
          Pos
        </td>
        <td className={`${styleCols.equipo}`}>Equipo</td>
        <td className={`${styleCols.partidos}`} title="Partidos Jugados">
          PJ
        </td>
        {!esMobile && (
          <td className={`${styleCols.organizador}`} title="Organizador">
            Org
          </td>
        )}
        {!modoConsulta && <td className={`${styleCols.titulo}`}>Título</td>}
        {!modoConsulta && <td className={`${styleCols.movimiento}`}>Mov</td>}
        {!esMobile && <td className={`${styleCols.observaciones}`}>Observaciones</td>}
        <td className={`${styles.editar} ${styleCols.botoneraAcciones}`}></td>
      </tr>
      {lista.map((item, index) => {
        const enEdicion = !!itemsEditando[item.id];
        if (enEdicion) {
          return (
            <FilaTorneoParticipanteEditable
              key={`edit-${item.id}`}
              item={itemsEditando[item.id]}
              torneo={torneo}
              equiposDisponibles={equiposDisponibles}
              esMobile={esMobile}
              onGuardar={onGuardarEdicion}
              onCancelar={onCancelarEdicionItem}
              onItemChange={(_, updatedItem) => onItemChangeEdicion(item.id, updatedItem)}
            />
          );
        }
        return (
          <tr className={`${styles.valores} ${esIdElegido(item.id) && styles.elegido}`} key={index}>
            <td className={`${styleCols.posicion}`}>{item.posicionFinal || ''}</td>
            <td className={`${styleCols.equipo}`}>
              {NombreEquipoTorneo(item.equipo || item, torneo)}
              {item && <IconoOrganizador objeto={item} />}
              {item && <IconoTitulo objeto={item} />}
              {item && <IconoMovimiento objeto={item} />}
            </td>
            <td className={`${styleCols.partidos}`}>{item.partidosJugados || ''}</td>
            {!esMobile && (
              <td className={`${styleCols.organizador}`}>{item.organizador ? '✓' : ''}</td>
            )}
            {!modoConsulta && (
              <td className={`${styleCols.titulo}`}>{item.tituloObtenido || ''}</td>
            )}
            {!modoConsulta && (
              <td className={`${styleCols.movimiento}`}>{item.movimientoDivisional || ''}</td>
            )}
            {!esMobile && (
              <td className={`${styleCols.observaciones}`}>{item.observaciones || ''}</td>
            )}
            <ColumnaBotoneraItem
              idItem={item.id}
              onEditarItem={onEditarItem}
              onEliminarItem={onEliminarItem}
              className={`${styleCols.botoneraAcciones}`}
            />
          </tr>
        );
      })}
      {onGuardarNuevo &&
        filasEditablesNecesarias > 0 &&
        equiposDisponibles !== null &&
        Array.from({ length: filasEditablesNecesarias }).map((_, index) => (
          <FilaTorneoParticipanteEditable
            key={`editable-${resetKey}-${index}`}
            item={null}
            index={index}
            torneo={torneo}
            equiposDisponibles={equiposDisponibles}
            onGuardar={onGuardarNuevo}
            onItemChange={onItemChangeHandler}
            onCancelar={null}
          />
        ))}
      {onGuardarTodo &&
        ((filasEditablesNecesarias > 0 && equiposDisponibles !== null) ||
          Object.keys(itemsEditando).length > 0) && (
          <tr>
            <td colSpan={colSpanGuardarTodo} className={`${styleCols.filaGuardarTodo}`}>
              <button
                className={`${styleCols.botonGuardarTodo}`}
                onClick={handleGuardarTodo}
                disabled={guardandoTodo || !hayDatosParaGuardar}
              >
                {guardandoTodo ? 'Guardando...' : 'Guardar todo'}
              </button>
            </td>
          </tr>
        )}
    </React.Fragment>
  );
};

export default TablaTorneoParticipantes;
